import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ConcreteHatchPattern } from './DraftingPrimitives';
import { explainImageUrls } from './PerspectiveViewer';

const INK = '#3A3C3E';
const RUB = '#77B72B';
const MUTED = '#4A4E55';
const PAPER = '#FFFFFF';
const PAPER2 = '#F3F5F4';
const GLASS = '#D8E4EE';
const FRAME = '#3A3C3E';

const GAP_MM = 15;
const OPENING_EXTRA_MM = GAP_MM * 2;
/** Alu-Rahmen außen = Bestellmaß + 125 mm je Seite. */
const FRAME_OVERHANG_MM = 125;
const FRAME_EXTRA_MM = FRAME_OVERHANG_MM * 2;

const EXPLAIN_SHOTS = [
  {
    id: 'schnitt' as const,
    label: 'Im Dach',
    caption:
      'Der Aufsatzkranz sitzt auf dem Dach, nicht in der Öffnung. Die Rohbaulichte ist das Loch in der Decke.',
  },
  {
    id: 'innen' as const,
    label: 'Von unten',
    caption:
      '15 mm je Seite bleiben frei, damit die Deckenlaibung verkleidet werden kann.',
  },
  {
    id: 'draufsicht' as const,
    label: 'Von oben',
    caption: 'Der Alu-Rahmen liegt auf dem Dach und ist größer als die Rohbaulichte.',
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
  /** Bestellmaß Breite (mm). */
  width: number;
  /** Bestellmaß Länge (mm). */
  length: number;
  /** Welches Maß der Nutzer gerade eingibt — steuert die Betonung. */
  mode: 'order' | 'opening';
}

function fmt(n: number): string {
  return Math.round(n).toLocaleString('de-DE');
}

function fitRect(widthMm: number, lengthMm: number, maxPx: number) {
  const ratio = widthMm / Math.max(1, lengthMm);
  if (ratio >= 1) {
    return { w: maxPx, l: maxPx / ratio };
  }
  return { w: maxPx * ratio, l: maxPx };
}

export default function OpeningExplainDialog({
  open,
  onClose,
  width,
  length,
  mode,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const hatchId = `sv-open-hatch-${useId().replace(/:/g, '')}`;
  const [shotId, setShotId] = useState<(typeof EXPLAIN_SHOTS)[number]['id']>('schnitt');

  useEffect(() => {
    if (!open) return;
    setShotId('schnitt');

    const previous = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 0);

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const nodes = panelRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      previous?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const host =
    typeof document !== 'undefined'
      ? document.querySelector('.skyvision-konfigurator') || document.body
      : null;
  if (!host) return null;

  const wMm = Math.max(1, width);
  const lMm = Math.max(1, length);
  const openW = wMm + OPENING_EXTRA_MM;
  const openL = lMm + OPENING_EXTRA_MM;
  const emphasizeOpening = mode === 'opening';

  const dialog = (
    <div className="sv-explain-layer">
      <button
        type="button"
        className="sv-explain-scrim"
        aria-label="Zeichnung schließen"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="sv-explain-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="sv-explain-head">
          <div>
            <h2 id={titleId} className="sv-h2 text-ink">
              Bestellmaß und Rohbaulichte
            </h2>
            <p className="sv-explain-lead">
              Fenstermaß ist das Bestellmaß: so groß fertigen wir das Fenster.
              Die Rohbaulichte in der Decke ist {OPENING_EXTRA_MM}&nbsp;mm
              größer. Die {GAP_MM}&nbsp;mm je Seite bleiben frei, damit die
              Deckenlaibung verkleidet werden kann. Der Alu-Rahmen liegt auf
              dem Dach und ist {FRAME_EXTRA_MM}&nbsp;mm größer als das
              Bestellmaß.
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="sv-explain-close"
            onClick={onClose}
          >
            Schließen
          </button>
        </header>

        <ExplainShots shotId={shotId} onShotId={setShotId} />

        <OpeningPlan
          widthMm={wMm}
          lengthMm={lMm}
          hatchId={hatchId}
          emphasizeOpening={emphasizeOpening}
        />

        <p className="sv-explain-scale">
          Der Spalt zwischen Bestellmaß und Rohbaulichte ist hier größer
          gezeichnet, damit man ihn sieht. In echt sind es {GAP_MM}&nbsp;mm je
          Seite für die Deckenlaibung.
        </p>

        <dl className="sv-explain-legend">
          <div className={`sv-explain-item ${emphasizeOpening ? '' : 'is-active'}`}>
            <dt>
              <span className="sv-explain-swatch is-glass" aria-hidden />
              Bestellmaß
            </dt>
            <dd>
              <span className="sv-mono tabular-nums">
                {fmt(wMm)} × {fmt(lMm)} mm
              </span>
              <span>Fenstermaß. Das fertigen wir, das steht auf der Bestellung.</span>
            </dd>
          </div>
          <div className={`sv-explain-item ${emphasizeOpening ? 'is-active' : ''}`}>
            <dt>
              <span className="sv-explain-swatch is-opening" aria-hidden />
              Rohbaulichte
            </dt>
            <dd>
              <span className="sv-mono tabular-nums">
                {fmt(openW)} × {fmt(openL)} mm
              </span>
              <span>
                {OPENING_EXTRA_MM}&nbsp;mm größer. {GAP_MM}&nbsp;mm je Seite
                für die Verkleidung der Deckenlaibung.
              </span>
            </dd>
          </div>
          <div className="sv-explain-item">
            <dt>
              <span className="sv-explain-swatch is-frame" aria-hidden />
              Alu-Rahmen außen
            </dt>
            <dd>
              <span className="sv-mono tabular-nums">
                {fmt(wMm + FRAME_EXTRA_MM)} × {fmt(lMm + FRAME_EXTRA_MM)} mm
              </span>
              <span>
                Bestellmaß + {FRAME_EXTRA_MM}&nbsp;mm. Liegt auf dem Dach, nicht
                in der Rohbaulichte.
              </span>
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );

  return createPortal(dialog, host);
}

function ExplainShots({
  shotId,
  onShotId,
}: {
  shotId: (typeof EXPLAIN_SHOTS)[number]['id'];
  onShotId: (id: (typeof EXPLAIN_SHOTS)[number]['id']) => void;
}) {
  const urls = explainImageUrls();
  const shot = EXPLAIN_SHOTS.find((item) => item.id === shotId) ?? EXPLAIN_SHOTS[0];

  return (
    <figure className="sv-explain-shots">
      <div role="tablist" aria-label="Erklärfoto" className="sv-view-toggle">
        {EXPLAIN_SHOTS.map((item) => {
          const active = item.id === shotId;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`sv-view-toggle-btn ${active ? 'is-active' : ''}`}
              onClick={() => onShotId(item.id)}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      <img
        className={`sv-explain-shot ${shot.id === 'draufsicht' ? 'is-square' : ''}`}
        src={urls[shot.id]}
        alt={`${shot.label}. ${shot.caption}`}
      />
      <figcaption className="sv-explain-shot-cap">{shot.caption}</figcaption>
    </figure>
  );
}

function OpeningPlan({
  widthMm,
  lengthMm,
  hatchId,
  emphasizeOpening,
}: {
  widthMm: number;
  lengthMm: number;
  hatchId: string;
  emphasizeOpening: boolean;
}) {
  const VB_W = 720;
  const VB_H = 500;
  const CX = 372;
  const CY = 248;

  const outerWmm = widthMm + FRAME_EXTRA_MM;
  const outerLmm = lengthMm + FRAME_EXTRA_MM;
  const openWmm = widthMm + OPENING_EXTRA_MM;
  const openLmm = lengthMm + OPENING_EXTRA_MM;

  const { w: outerW, l: outerL } = fitRect(outerWmm, outerLmm, 236);
  const px = outerW / outerWmm;
  const winW = widthMm * px;
  const winL = lengthMm * px;
  const frameOverhang = FRAME_OVERHANG_MM * px;
  const gap = Math.min(
    frameOverhang * 0.42,
    Math.max(GAP_MM * px, Math.min(winW, winL) * 0.06),
  );

  const xFrame = CX - outerW / 2;
  const yFrame = CY - outerL / 2;
  const xWin = CX - winW / 2;
  const yWin = CY - winL / 2;
  const xOpen = CX - (winW / 2 + gap);
  const yOpen = CY - (winL / 2 + gap);
  const openW = winW + gap * 2;
  const openL = winL + gap * 2;

  const band = 40;
  const xRoof = xFrame - band;
  const yRoof = yFrame - band;
  const roofW = outerW + band * 2;
  const roofH = outerL + band * 2;

  const openColor = emphasizeOpening ? RUB : INK;
  const winColor = emphasizeOpening ? INK : RUB;

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      className="sv-explain-plan"
      role="img"
      aria-label={
        `Draufsicht: Bestellmaß ${fmt(widthMm)} mal ${fmt(lengthMm)} Millimeter, ` +
        `Rohbaulichte ${fmt(openWmm)} mal ${fmt(openLmm)} Millimeter, ` +
        `Alu-Rahmen außen ${fmt(outerWmm)} mal ${fmt(outerLmm)} Millimeter.`
      }
    >
      <ConcreteHatchPattern id={hatchId} tone="muted" />

      {/* Dachfläche */}
      <rect
        x={xRoof}
        y={yRoof}
        width={roofW}
        height={roofH}
        fill={PAPER2}
        stroke={MUTED}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <rect
        x={xRoof}
        y={yRoof}
        width={roofW}
        height={roofH}
        fill={`url(#${hatchId})`}
      />

      {/* Loch aus der Dachfläche schneiden */}
      <rect
        x={xOpen}
        y={yOpen}
        width={openW}
        height={openL}
        fill={PAPER}
        stroke="none"
      />

      {/* Alu-Rahmen — Außenmaß, liegt auf dem Dach */}
      <rect
        x={xFrame}
        y={yFrame}
        width={outerW}
        height={outerL}
        fill={FRAME}
        stroke={INK}
        strokeWidth={1.1}
        vectorEffect="non-scaling-stroke"
      />

      {/* Bestellmaß — lichte Öffnung */}
      <rect
        x={xWin}
        y={yWin}
        width={winW}
        height={winL}
        fill={GLASS}
        stroke={winColor}
        strokeWidth={emphasizeOpening ? 1.1 : 1.6}
        vectorEffect="non-scaling-stroke"
      />

      {/* Rohbaulichte — gestrichelt auf dem Rahmen, immer rubric damit sie auf Schwarz lesbar bleibt */}
      <rect
        x={xOpen}
        y={yOpen}
        width={openW}
        height={openL}
        fill="none"
        stroke={RUB}
        strokeWidth={emphasizeOpening ? 1.6 : 1.2}
        strokeDasharray="5 3.5"
        vectorEffect="non-scaling-stroke"
      />

      {Math.min(winW, winL) > 88 && (
        <text
          x={CX}
          y={CY + 4}
          textAnchor="middle"
          fill={INK}
          fontFamily="'Config Condensed', sans-serif"
          fontSize={13}
          fontWeight={500}
        >
          Bestellmaß
        </text>
      )}

      <text
        x={xRoof + 10}
        y={yRoof + 18}
        fill={MUTED}
        fontFamily="'Config Condensed', sans-serif"
        fontSize={12}
      >
        Dachfläche
      </text>

      <GapDim x={xWin + winW} y={CY} gap={gap} color={RUB} />

      <DimH
        x1={xFrame}
        x2={xFrame + outerW}
        y={yRoof - 22}
        label={`Alu-Rahmen ${fmt(outerWmm)} mm`}
        color={INK}
      />
      <DimH
        x1={xWin}
        x2={xWin + winW}
        y={yRoof + roofH + 22}
        label={`Bestellmaß ${fmt(widthMm)} mm`}
        color={winColor}
        below
      />
      <DimV
        x={xRoof - 22}
        y1={yOpen}
        y2={yOpen + openL}
        label={`Rohbaulichte ${fmt(openLmm)} mm`}
        color={openColor}
      />
      {lengthMm !== widthMm && (
        <DimV
          x={xRoof + roofW + 20}
          y1={yWin}
          y2={yWin + winL}
          label={`Bestellmaß ${fmt(lengthMm)} mm`}
          color={winColor}
        />
      )}
    </svg>
  );
}

function GapDim({
  x,
  y,
  gap,
  color,
}: {
  x: number;
  y: number;
  gap: number;
  color: string;
}) {
  const x2 = x + gap;
  return (
    <g>
      <line
        x1={x}
        y1={y}
        x2={x2}
        y2={y}
        stroke={color}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={x}
        y1={y - 4}
        x2={x}
        y2={y + 4}
        stroke={color}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={x2}
        y1={y - 4}
        x2={x2}
        y2={y + 4}
        stroke={color}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <text
        x={x2 + 8}
        y={y + 4}
        textAnchor="start"
        fill={color}
        fontFamily="'Config Condensed', sans-serif"
        fontSize={12}
        fontWeight={500}
      >
        {GAP_MM} mm Laibung
      </text>
    </g>
  );
}

function DimH({
  x1,
  x2,
  y,
  label,
  color,
  below = false,
}: {
  x1: number;
  x2: number;
  y: number;
  label: string;
  color: string;
  below?: boolean;
}) {
  const tick = 4;
  return (
    <g stroke={color} fill={color}>
      <line
        x1={x1}
        y1={y}
        x2={x2}
        y2={y}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={x1 - tick}
        y1={y + tick}
        x2={x1 + tick}
        y2={y - tick}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={x2 - tick}
        y1={y + tick}
        x2={x2 + tick}
        y2={y - tick}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <text
        x={(x1 + x2) / 2}
        y={below ? y + 16 : y - 8}
        textAnchor="middle"
        fontFamily="'Config Condensed', sans-serif"
        fontSize={13}
        fontWeight={500}
        stroke="none"
      >
        {label}
      </text>
    </g>
  );
}

function DimV({
  x,
  y1,
  y2,
  label,
  color,
}: {
  x: number;
  y1: number;
  y2: number;
  label: string;
  color: string;
}) {
  const tick = 4;
  const mid = (y1 + y2) / 2;
  return (
    <g stroke={color} fill={color}>
      <line
        x1={x}
        y1={y1}
        x2={x}
        y2={y2}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={x - tick}
        y1={y1 - tick}
        x2={x + tick}
        y2={y1 + tick}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={x - tick}
        y1={y2 - tick}
        x2={x + tick}
        y2={y2 + tick}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <text
        x={x}
        y={mid}
        textAnchor="middle"
        fontFamily="'Config Condensed', sans-serif"
        fontSize={13}
        fontWeight={500}
        stroke="none"
        transform={`rotate(-90 ${x} ${mid})`}
        dy={-8}
      >
        {label}
      </text>
    </g>
  );
}
